// Pure viewport maths: screen = unit * scale + pan. All sizes in CSS pixels.

import type { Bounds, Point } from './minimapGeometry.js';

export interface Viewport {
  readonly scale: number;
  readonly panX: number;
  readonly panY: number;
}

export interface CanvasSize {
  readonly width: number;
  readonly height: number;
}

/** Zoom limits relative to the fit scale. */
export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 8;
/** Wheel/button zoom step. */
export const ZOOM_STEP = 1.25;
/** Minimum hex circumradius (px) ever used, whatever the board size. */
export const MIN_HEX_PX = 1;
const PADDING = 4;

export function fitScaleFor(bounds: Bounds, size: CanvasSize, padding = PADDING): number {
  const bw = Math.max(bounds.maxX - bounds.minX, 1e-6);
  const bh = Math.max(bounds.maxY - bounds.minY, 1e-6);
  const aw = Math.max(size.width - 2 * padding, 1);
  const ah = Math.max(size.height - 2 * padding, 1);
  return Math.max(MIN_HEX_PX, Math.min(aw / bw, ah / bh));
}

/** The viewport showing the whole board, centred. */
export function fitBoard(bounds: Bounds, size: CanvasSize, padding = PADDING): Viewport {
  const scale = fitScaleFor(bounds, size, padding);
  const cx = (bounds.minX + bounds.maxX) / 2;
  const cy = (bounds.minY + bounds.maxY) / 2;
  return { scale, panX: size.width / 2 - cx * scale, panY: size.height / 2 - cy * scale };
}

export function unitToScreen(vp: Viewport, x: number, y: number): Point {
  return { x: x * vp.scale + vp.panX, y: y * vp.scale + vp.panY };
}

export function screenToUnit(vp: Viewport, sx: number, sy: number): Point {
  return { x: (sx - vp.panX) / vp.scale, y: (sy - vp.panY) / vp.scale };
}

/**
 * Keep the board from leaving the view: on an axis where the board is smaller than the view
 * it is centred; otherwise at least `keep` px of it must stay on screen on both ends.
 */
export function clampPan(vp: Viewport, bounds: Bounds, size: CanvasSize, keep = 48): Viewport {
  const axis = (min: number, max: number, extent: number, pan: number): number => {
    const lo = min * vp.scale;
    const hi = max * vp.scale;
    if (hi - lo <= extent) return extent / 2 - (lo + hi) / 2;
    const k = Math.min(keep, extent / 2);
    const maxPan = extent - k - lo; // board's near edge at most extent-k
    const minPan = k - hi; // board's far edge at least k
    return Math.min(maxPan, Math.max(minPan, pan));
  };
  return {
    scale: vp.scale,
    panX: axis(bounds.minX, bounds.maxX, size.width, vp.panX),
    panY: axis(bounds.minY, bounds.maxY, size.height, vp.panY),
  };
}

export function clampScale(scale: number, fitScale: number): number {
  return Math.min(fitScale * MAX_ZOOM, Math.max(fitScale * MIN_ZOOM, MIN_HEX_PX, scale));
}

/** Zoom by `factor` keeping the unit-space point under `anchor` (screen px) fixed. */
export function zoomAt(
  vp: Viewport,
  factor: number,
  anchor: Point,
  bounds: Bounds,
  size: CanvasSize,
): Viewport {
  const fit = fitScaleFor(bounds, size);
  const scale = clampScale(vp.scale * factor, fit);
  const k = scale / vp.scale;
  return clampPan(
    { scale, panX: anchor.x - (anchor.x - vp.panX) * k, panY: anchor.y - (anchor.y - vp.panY) * k },
    bounds,
    size,
  );
}

/** Pan by a screen delta, clamped. */
export function panBy(
  vp: Viewport,
  dx: number,
  dy: number,
  bounds: Bounds,
  size: CanvasSize,
): Viewport {
  return clampPan({ ...vp, panX: vp.panX + dx, panY: vp.panY + dy }, bounds, size);
}

/** Pan the minimal amount so unit-space point (x, y) is at least `margin` px inside the view. */
export function revealPoint(
  vp: Viewport,
  x: number,
  y: number,
  bounds: Bounds,
  size: CanvasSize,
  margin: number,
): Viewport {
  const sx = x * vp.scale + vp.panX;
  const sy = y * vp.scale + vp.panY;
  const m = Math.min(margin, size.width / 2, size.height / 2);
  const dx = sx < m ? m - sx : sx > size.width - m ? size.width - m - sx : 0;
  const dy = sy < m ? m - sy : sy > size.height - m ? size.height - m - sy : 0;
  return dx === 0 && dy === 0 ? vp : panBy(vp, dx, dy, bounds, size);
}

/** Zoom level shown to the player: 1 = whole board. */
export function zoomLevel(vp: Viewport, bounds: Bounds, size: CanvasSize): number {
  return vp.scale / fitScaleFor(bounds, size);
}

export function viewportEquals(a: Viewport, b: Viewport): boolean {
  return (
    Math.abs(a.scale - b.scale) < 1e-6 &&
    Math.abs(a.panX - b.panX) < 1e-3 &&
    Math.abs(a.panY - b.panY) < 1e-3
  );
}
